import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useIsDemo } from '@/context/DemoContext';
import { useDemoGuard } from '@/components/DemoGuard';
import PlayerCodeModal from '@/components/PlayerCodeModal';
import { AGE_DIVISIONS } from '@/components/CoachLayout';
import { 
  Plus, Edit, Trash2, UserPlus, Archive,
  Users, Building2, CalendarPlus, RefreshCw, Star, Link as LinkIcon
} from 'lucide-react';

const C = {
  orange: '#F5A623', orangeDark: '#D4891A',
  navy: '#0D1B26', navyCard: '#1A2B3C',
  green: '#27AE60', red: '#E74C3C',
  muted: '#8FA3B1', border: '#DDE5EC', bg: '#F0F3F6',
};

const VALID_THEME_COLORS = [
  "#3B82F6", "#10B981", "#8B5CF6", "#F97316", "#EF4444", "#14B8A6", "#EC4899", "#64748B",
];

export default function TeamsManagement() {
  const ctx = useOutletContext() || {};
  const { 
    teams = [], selectedTeamId, setSelectedTeamId, 
    seasons = [], selectedSeasonId, setSelectedSeasonId,
    fetchTeams, setShowCreateTeamDialog, isStaff,
  } = ctx;

  const isDemo = useIsDemo();
  const demoBlocked = useDemoGuard();

  const [activeTab, setActiveTab] = useState('roster'); // roster only — tabs removed
  const [showTeamInfoDialog, setShowTeamInfoDialog] = useState(false);
  const [seasonTrackingEnabled, setSeasonTrackingEnabled] = useState(false);
  const [currentSeasonName, setCurrentSeasonName] = useState('');
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [archivedTeams, setArchivedTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localSeasons, setLocalSeasons] = useState([]);

  // Player dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [playerName, setPlayerName] = useState('');
  const [jerseyNumber, setJerseyNumber] = useState('');

  // Team edit state
  const [editingTeam, setEditingTeam] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: '', gender: '', age_division: '', home_venue: '', theme_color: '' });

  // Archive confirmation
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archivingTeamId, setArchivingTeamId] = useState(null);

  // Player link-code modal
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [codeModalPlayer, setCodeModalPlayer] = useState(null);
  const [codeModalCode, setCodeModalCode] = useState('');
  const [codeModalLoading, setCodeModalLoading] = useState(false);

  const openCodeModal = async (player) => {
    setCodeModalPlayer(player);
    setCodeModalCode('');
    setCodeModalOpen(true);
    setCodeModalLoading(true);
    try {
      const r = await api.get(`/players/${player.player_id}/code`);
      setCodeModalCode(r.data?.code || '');
    } catch {
      toast.error('Could not generate code');
      setCodeModalOpen(false);
    } finally {
      setCodeModalLoading(false);
    }
  };

  // Season creation
  const [showCreateSeasonDialog, setShowCreateSeasonDialog] = useState(false);
  const [newSeasonName, setNewSeasonName] = useState('');
  const [rolloverRoster, setRolloverRoster] = useState(true);
  const [rolloverFromSeasonId, setRolloverFromSeasonId] = useState(null);
  const [creatingSeason, setCreatingSeason] = useState(false);

  // Use local seasons or context seasons
  const displaySeasons = localSeasons.length > 0 ? localSeasons : seasons;

  useEffect(() => {
    if (selectedTeamId) {
      fetchTeam();
      fetchLocalSeasons();
    }
  }, [selectedTeamId]);

  useEffect(() => {
    if (selectedTeamId && selectedSeasonId) {
      fetchPlayers();
    } else if (selectedTeamId && !selectedSeasonId && displaySeasons.length > 0) {
      // Auto-select first season if none selected
      setSelectedSeasonId(displaySeasons[0].season_id);
    }
  }, [selectedTeamId, selectedSeasonId, displaySeasons]);

  useEffect(() => {
    if (activeTab === 'archived') {
      fetchArchivedTeams();
    }
  }, [activeTab]);

  const fetchTeam = async () => {
    try {
      const r = await api.get(`/teams/${selectedTeamId}`);
      setTeam(r.data);
      setTeamForm({
        name: r.data.name || '',
        gender: r.data.gender || '',
        age_division: r.data.age_division || '',
        home_venue: r.data.home_venue || '',
        theme_color: r.data.theme_color || '',
      });
      setSeasonTrackingEnabled(!!r.data.season_tracking_enabled);
      setCurrentSeasonName(r.data.current_season_name || '');
    } catch (e) {
      toast.error('Failed to fetch team');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocalSeasons = async () => {
    try {
      const r = await api.get(`/teams/${selectedTeamId}/seasons`);
      let seasonsList = r.data;
      // If no seasons exist for this team, silently create a default one so
      // the roster has somewhere to live. Season tracking is hidden from the
      // UI by default — users only opt in via the Team Info toggle.
      if (seasonsList.length === 0) {
        try {
          const created = await api.post(`/teams/${selectedTeamId}/seasons`, {
            name: 'Default',
          });
          seasonsList = [created.data];
        } catch {
          // If we can't create one (e.g., demo user), fall through with empty list.
        }
      }
      setLocalSeasons(seasonsList);
      if (seasonsList.length > 0 && !selectedSeasonId) {
        setSelectedSeasonId(seasonsList[0].season_id);
      }
    } catch (e) {
      console.error('Failed to fetch seasons:', e);
    }
  };

  const fetchPlayers = async () => {
    if (!selectedSeasonId) {
      setPlayers([]);
      return;
    }
    try {
      const r = await api.get(`/teams/${selectedTeamId}/players?season_id=${selectedSeasonId}`);
      setPlayers(r.data);
    } catch (e) {
      toast.error('Failed to fetch players');
    }
  };

  const fetchArchivedTeams = async () => {
    try {
      const r = await api.get('/teams/archived');
      setArchivedTeams(r.data);
    } catch (e) {
      console.error('Failed to fetch archived teams:', e);
    }
  };

  // Team handlers
  const handleSaveTeam = async () => {
    if (demoBlocked('edit your team details')) return;
    try {
      const payload = {
        name: teamForm.name,
        gender: teamForm.gender || null,           // null clears it; '' would 422 the Literal
        age_division: teamForm.age_division || null,
        home_venue: teamForm.home_venue,
        theme_color: teamForm.theme_color,
        season_tracking_enabled: seasonTrackingEnabled,
        current_season_name: seasonTrackingEnabled ? (currentSeasonName.trim() || null) : null,
      };
      await api.put(`/teams/${selectedTeamId}`, payload);
      toast.success('Team updated');
      setEditingTeam(false);
      setShowTeamInfoDialog(false);
      fetchTeam();
      fetchTeams();
    } catch (e) {
      toast.error('Failed to update team');
    }
  };

  const handleArchiveTeam = async () => {
    if (demoBlocked('archive your teams')) return;
    try {
      await api.post(`/teams/${archivingTeamId}/archive`);
      toast.success('Team archived');
      setShowArchiveConfirm(false);
      setArchivingTeamId(null);
      fetchTeams();
      const remainingTeams = teams.filter(t => t.team_id !== archivingTeamId);
      if (remainingTeams.length > 0) {
        setSelectedTeamId(remainingTeams[0].team_id);
      }
    } catch (e) {
      toast.error('Failed to archive team');
    }
  };

  const handleUnarchiveTeam = async (teamId) => {
    if (demoBlocked('restore your teams')) return;
    try {
      await api.post(`/teams/${teamId}/unarchive`);
      toast.success('Team restored');
      fetchArchivedTeams();
      fetchTeams();
    } catch (e) {
      toast.error('Failed to restore team');
    }
  };

  // Season handlers
  const openCreateSeasonDialog = () => {
    if (demoBlocked('create new seasons for your teams')) return;
    setNewSeasonName('');
    setRolloverRoster(displaySeasons.length > 0); // Default to rollover if seasons exist
    setRolloverFromSeasonId(displaySeasons.length > 0 ? displaySeasons[0].season_id : null);
    setShowCreateSeasonDialog(true);
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();
    if (demoBlocked('create new seasons for your teams')) return;
    if (!newSeasonName.trim()) return;
    
    setCreatingSeason(true);
    try {
      const payload = { 
        name: newSeasonName.trim(),
        rollover_from_season_id: rolloverRoster ? rolloverFromSeasonId : null
      };
      
      const r = await api.post(`/teams/${selectedTeamId}/seasons`, payload);
      
      if (r.data.players_copied > 0) {
        toast.success(`Season "${newSeasonName}" created with ${r.data.players_copied} players rolled over`);
      } else {
        toast.success(`Season "${newSeasonName}" created`);
      }
      
      setShowCreateSeasonDialog(false);
      setNewSeasonName('');
      await fetchLocalSeasons();
      setSelectedSeasonId(r.data.season_id);
    } catch (e) {
      toast.error('Failed to create season');
    } finally {
      setCreatingSeason(false);
    }
  };

  // Player handlers
  const handleAddPlayer = async (e) => {
    e.preventDefault();
    if (demoBlocked('add players to your roster')) return;
    if (!selectedSeasonId) {
      toast.error('Please select a season first');
      return;
    }
    try {
      await api.post(`/teams/${selectedTeamId}/players`, {
        name: playerName,
        jersey_number: jerseyNumber,
        season_id: selectedSeasonId,
      });
      // No toast — the player appearing in the roster IS the confirmation.
      setShowAddDialog(false);
      setPlayerName('');
      setJerseyNumber('');
      fetchPlayers();
    } catch (e) {
      toast.error('Failed to add player');
    }
  };

  const handleEditPlayer = async (e) => {
    e.preventDefault();
    if (demoBlocked('edit player details')) return;
    try {
      await api.patch(`/players/${editingPlayer.player_id}`, {
        name: playerName,
        jersey_number: jerseyNumber,
      });
      toast.success('Player updated');
      setShowEditDialog(false);
      setEditingPlayer(null);
      fetchPlayers();
    } catch (e) {
      toast.error('Failed to update player');
    }
  };

  const handleDeletePlayer = async (playerId) => {
    if (demoBlocked('manage your roster')) return;
    if (!confirm('Are you sure you want to remove this player from this season?')) return;
    try {
      await api.delete(`/players/${playerId}`);
      toast.success('Player removed');
      fetchPlayers();
    } catch (e) {
      toast.error('Failed to remove player');
    }
  };

  const openEditPlayer = (player) => {
    setEditingPlayer(player);
    setPlayerName(player.name);
    setJerseyNumber(player.jersey_number || '');
    setShowEditDialog(true);
  };

  const selectedSeason = displaySeasons.find(s => s.season_id === selectedSeasonId);

  if (!selectedTeamId) {
    return (
      <div className="text-center py-12">
        <Users className="w-16 h-16 mx-auto mb-4" style={{ color: C.muted }} />
        <h2 className="text-xl font-bold mb-2" style={{ color: C.navy, fontFamily: "'Barlow Condensed', sans-serif" }}>
          No Team Selected
        </h2>
        <p className="text-sm mb-4" style={{ color: C.muted }}>
          Create a team to get started
        </p>
        <button
          type="button"
          onClick={() => { if (demoBlocked('create and manage your own teams')) return; setShowCreateTeamDialog(true); }}
          data-testid="create-team-empty-cta"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-[15px] uppercase tracking-[.07em] transition-all hover:opacity-90 active:scale-[.98]"
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            background: C.orange,
            color: C.navy,
            border: 'none',
            display: isStaff ? 'none' : undefined,
          }}
        >
          <Plus className="w-5 h-5" /> Create Team
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-extrabold uppercase tracking-tight text-slate-900 mb-5"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="teams-title">
        Teams
      </h1>

      {/* Compact Page Header — single row with team dropdown + edit pencil */}
      <div className="flex items-center gap-3 mb-6" data-testid="teams-header">
        <div className="flex-1 flex items-center gap-2">
          <Select value={selectedTeamId} onValueChange={(val) => {
            if (val === 'create_new') {
              if (demoBlocked('create and manage your own teams')) return;
              setShowCreateTeamDialog(true);
            } else {
              setSelectedTeamId(val);
            }
          }}>
            <SelectTrigger
              data-testid="team-selector-teams-page"
              className="h-10 border-[1.5px] rounded-[7px] gap-2 text-[1rem] font-bold uppercase tracking-[.03em] flex-1 min-w-0"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", background: '#fff', borderColor: C.border, color: C.navy }}
            >
              <SelectValue placeholder="Select team..." />
            </SelectTrigger>
            <SelectContent>
              {teams.map(t => (
                <SelectItem key={t.team_id} value={t.team_id}>
                  {t.name}{t.level ? ` (${t.level})` : ''}
                </SelectItem>
              ))}
              {!isStaff && (
                <SelectItem value="create_new" className="text-blue-600 font-bold border-t">
                  <span className="flex items-center"><Plus className="w-3.5 h-3.5 mr-1.5" />Create New Team</span>
                </SelectItem>
              )}
            </SelectContent>
          </Select>

          {!isStaff && team && (
            <button
              onClick={() => setShowTeamInfoDialog(true)}
              data-testid="team-info-edit-button"
              className="w-10 h-10 rounded-[7px] flex items-center justify-center transition-all hover:bg-slate-100 active:scale-95"
              style={{ border: `1.5px solid ${C.border}`, background: '#fff', color: C.navy }}
              aria-label="Edit team info"
            >
              <Edit className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
        
      {/* Tab content (roster only — tabs removed in single-screen redesign) */}
      {activeTab === 'roster' && (
        <div className="bg-white rounded-xl p-6" style={{ border: `1.5px solid ${C.border}` }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold uppercase" style={{ color: C.navy, fontFamily: "'Barlow Condensed', sans-serif" }}>
                Roster
              </h3>
              <p className="text-sm" style={{ color: C.muted }}>{players.length} players</p>
            </div>
            <Button 
              onClick={() => { if (demoBlocked('add players to your roster')) return; setPlayerName(''); setJerseyNumber(''); setShowAddDialog(true); }} 
              className="rounded-full text-[15px] font-semibold uppercase tracking-[.07em]"
              style={{ background: C.orange, color: C.navy, fontFamily: "'Barlow Condensed', sans-serif" }}
              disabled={!selectedSeasonId}
              data-testid="add-player-button"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Add Player
            </Button>
          </div>

          {!selectedSeasonId ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-3" style={{ color: C.muted }} />
              <p style={{ color: C.muted }}>Loading roster…</p>
            </div>
          ) : players.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-3" style={{ color: C.muted }} />
              <p style={{ color: C.muted }}>No players on the roster yet</p>
              <p className="text-xs mt-1" style={{ color: C.muted }}>Tap "Add Player" to start building your roster.</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: C.border }}>
              {players.map(player => (
                <div key={player.player_id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-base font-extrabold"
                      style={{
                        background: '#ffa133',
                        color: '#0B1824',
                        fontFamily: "'Barlow Condensed', sans-serif",
                        letterSpacing: '0.02em',
                      }}
                      data-testid={`jersey-bubble-${player.player_id}`}
                    >
                      #{player.jersey_number || '?'}
                    </div>
                    <div>
                      <span className="font-semibold" style={{ color: C.navy, fontFamily: "'Barlow Condensed', sans-serif" }}>
                        {player.name}
                      </span>
                      {(player.user_id || player.fan_user_id || player.claimed_by_fan) && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ background: C.bg, color: C.green }}>
                          Linked
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 items-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        if (demoBlocked('flag a favorite player')) return;
                        try {
                          const r = await api.post(`/teams/${team.team_id}/players/${player.player_id}/favorite`);
                          // Multi-favorite: only flip THIS row, leave the
                          // other players' is_favorite untouched.
                          setPlayers(prev => prev.map(p => (
                            p.player_id === player.player_id
                              ? { ...p, is_favorite: r.data.is_favorite }
                              : p
                          )));
                          toast.success(r.data.is_favorite ? `${player.name} added to favorites` : 'Removed from favorites');
                        } catch {
                          toast.error('Could not update favorite');
                        }
                      }}
                      title={player.is_favorite ? 'Favorite player' : 'Mark as favorite'}
                      data-testid={`favorite-toggle-${player.player_id}`}
                    >
                      <Star
                        className="w-4 h-4"
                        style={{
                          color: player.is_favorite ? '#ffa133' : '#94a3b8',
                          fill: player.is_favorite ? '#ffa133' : 'none',
                        }}
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openCodeModal(player)}
                      title="Get player link code"
                      data-testid={`link-code-${player.player_id}`}
                    >
                      <LinkIcon className="w-4 h-4" style={{ color: '#94a3b8' }} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { if (demoBlocked('edit player details')) return; openEditPlayer(player); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { if (demoBlocked('manage your roster')) return; handleDeletePlayer(player.player_id); }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Team Info Dialog (replaces the old "Team Info" tab) */}
      <Dialog open={showTeamInfoDialog} onOpenChange={setShowTeamInfoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Team Info
            </DialogTitle>
          </DialogHeader>
          {team && (
            <div className="py-2 space-y-5">
              {/* Editable fields */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: C.muted }}>Team name</Label>
                <Input value={teamForm.name} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} className="h-10" data-testid="team-info-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: C.muted }}>Gender</Label>
                  <Select value={teamForm.gender || ''} onValueChange={(v) => setTeamForm({ ...teamForm, gender: v })}>
                    <SelectTrigger className="h-10" data-testid="team-info-gender">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="boys">Boys</SelectItem>
                      <SelectItem value="girls">Girls</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: C.muted }}>Age division</Label>
                  <Select value={teamForm.age_division || ''} onValueChange={(v) => setTeamForm({ ...teamForm, age_division: v })}>
                    <SelectTrigger className="h-10" data-testid="team-info-age-division">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {AGE_DIVISIONS.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: C.muted }}>Home venue (optional)</Label>
                <Input value={teamForm.home_venue} onChange={(e) => setTeamForm({ ...teamForm, home_venue: e.target.value })} className="h-10" data-testid="team-info-venue" />
              </div>

              {/* Season tracking — pill toggle (build 191).
                  Two pills inside a single rounded container; the active
                  pill carries the navy fill so its state is unambiguous
                  at a glance. Replaces the iOS-style switch which was
                  hard to read at small sizes. */}
              <div className="rounded-lg p-3" style={{ border: `1.5px solid ${C.border}` }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: C.navy }}>Organize games by season</p>
                  <p className="text-xs mb-3" style={{ color: C.muted }}>Off by default. Tag new games with a season label.</p>

                  <div
                    className="grid grid-cols-2 gap-1 rounded-full p-1"
                    style={{ background: '#F1F5F9' }}
                    role="radiogroup"
                    aria-label="Season tracking"
                  >
                    {[
                      { value: false, label: 'Off' },
                      { value: true,  label: 'On'  },
                    ].map(opt => {
                      const active = seasonTrackingEnabled === opt.value;
                      return (
                        <button
                          key={String(opt.value)}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => setSeasonTrackingEnabled(opt.value)}
                          data-testid={`season-tracking-pill-${opt.value ? 'on' : 'off'}`}
                          className="h-9 rounded-full text-[13px] font-bold uppercase tracking-[.08em] transition-colors active:scale-[.98]"
                          style={{
                            background: active ? '#0B1824' : 'transparent',
                            color: active ? '#fff' : '#475569',
                            fontFamily: "'Barlow Condensed', sans-serif",
                            border: 'none',
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {seasonTrackingEnabled && (
                  <div className="mt-3">
                    <Label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: C.muted }}>
                      Current season name
                    </Label>
                    <Input
                      value={currentSeasonName}
                      onChange={(e) => setCurrentSeasonName(e.target.value)}
                      placeholder="e.g. Spring 2026"
                      className="h-10"
                      data-testid="current-season-name-input"
                    />
                  </div>
                )}
              </div>

              {/* Archive */}
              {!isStaff && (
                <div className="pt-3 border-t" style={{ borderColor: C.border }}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => { if (demoBlocked('archive your teams')) return; setArchivingTeamId(selectedTeamId); setShowArchiveConfirm(true); setShowTeamInfoDialog(false); }}
                    data-testid="archive-team-button"
                  >
                    <Archive className="w-4 h-4 mr-1" /> Archive team
                  </Button>
                  <p className="text-xs mt-1" style={{ color: C.muted }}>
                    Archiving hides the team but preserves all data.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowTeamInfoDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveTeam} style={{ background: '#0B1824', color: '#fff' }} data-testid="team-info-save">
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Player Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Add Player to {selectedSeason?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPlayer}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Player Name *</Label>
                <Input value={playerName} onChange={(e) => setPlayerName(e.target.value)} required />
              </div>
              <div>
                <Label>Jersey Number</Label>
                <Input value={jerseyNumber} onChange={(e) => setJerseyNumber(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button type="submit" style={{ background: C.navy }}>Add Player</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Player Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Edit Player</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditPlayer}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Player Name *</Label>
                <Input value={playerName} onChange={(e) => setPlayerName(e.target.value)} required />
              </div>
              <div>
                <Label>Jersey Number</Label>
                <Input value={jerseyNumber} onChange={(e) => setJerseyNumber(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit" style={{ background: C.navy }}>Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Season Dialog */}
      <Dialog open={showCreateSeasonDialog} onOpenChange={setShowCreateSeasonDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              <CalendarPlus className="w-5 h-5 inline mr-2" />
              Create New Season
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSeason}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Season Name *</Label>
                <Input 
                  value={newSeasonName} 
                  onChange={(e) => setNewSeasonName(e.target.value)} 
                  placeholder="e.g., Spring 2026"
                  required 
                />
              </div>
              
              {displaySeasons.length > 0 && (
                <div className="p-4 rounded-lg" style={{ background: C.bg }}>
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="rollover" 
                      checked={rolloverRoster}
                      onCheckedChange={setRolloverRoster}
                    />
                    <div className="flex-1">
                      <label htmlFor="rollover" className="text-sm font-semibold cursor-pointer" style={{ color: C.navy }}>
                        <RefreshCw className="w-4 h-4 inline mr-1" />
                        Rollover roster from previous season
                      </label>
                      <p className="text-xs mt-1" style={{ color: C.muted }}>
                        Copy all players to the new season. You can remove players after.
                      </p>
                    </div>
                  </div>
                  
                  {rolloverRoster && (
                    <div className="mt-3">
                      <Label className="text-xs">Copy roster from:</Label>
                      <Select value={rolloverFromSeasonId || ''} onValueChange={setRolloverFromSeasonId}>
                        <SelectTrigger className="mt-1 h-9">
                          <SelectValue placeholder="Select season..." />
                        </SelectTrigger>
                        <SelectContent>
                          {displaySeasons.map(s => (
                            <SelectItem key={s.season_id} value={s.season_id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateSeasonDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={creatingSeason || !newSeasonName.trim()} style={{ background: C.navy }}>
                {creatingSeason ? 'Creating...' : 'Create Season'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation */}
      <AlertDialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Team?</AlertDialogTitle>
            <AlertDialogDescription>
              This will hide the team from your active teams list. All data will be preserved and you can restore it later from the "Archived Teams" tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveTeam} style={{ background: C.red }}>
              Archive Team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PlayerCodeModal
        open={codeModalOpen}
        onOpenChange={setCodeModalOpen}
        code={codeModalCode}
        playerName={codeModalPlayer?.name}
        teamName={team?.name}
        loading={codeModalLoading}
      />
    </div>
  );
}
